/**
 * Semantic Color Token System
 * 
 * This file defines semantic color tokens that automatically adapt to light/dark mode.
 * These tokens provide a consistent, scalable color system across the entire application.
 * 
 * Supports 4 intensity levels:
 * - light: Bright, high contrast light mode
 * - soft-light: Warmer, softer light mode with slightly muted backgrounds
 * - soft-dark: Softer dark mode with lighter backgrounds (easier on eyes)
 * - dark: Full dark mode with deepest backgrounds
 */

import { PaletteMode } from '@mui/material';

// Extended theme mode type
export type ThemeIntensity = 'light' | 'soft-light' | 'soft-dark' | 'dark';

// ============================================================================
// COLOR DEFINITIONS
// ============================================================================

/**
 * Base color palette - raw color values
 * These are the foundation colors that semantic tokens will reference
 */
const baseColors = {
  // Primary brand colors
  blue: {
    50: '#e3f2fd',
    100: '#bbdefb',
    200: '#90caf9',
    300: '#64b5f6',
    400: '#42a5f5',
    500: '#2E86AB', // Main brand blue
    600: '#1e88e5',
    700: '#1976d2',
    800: '#1565c0',
    900: '#0d47a1',
  },
  purple: {
    50: '#f3e5f5',
    100: '#e1bee7',
    200: '#ce93d8',
    300: '#ba68c8',
    400: '#ab47bc',
    500: '#A23B72', // Main brand purple
    600: '#8e24aa',
    700: '#7b1fa2',
    800: '#6a1b9a',
    900: '#4a148c',
  },
  
  // Semantic colors
  green: {
    50: '#e8f5e9',
    100: '#c8e6c9',
    200: '#a5d6a7',
    300: '#81c784',
    400: '#66bb6a',
    500: '#4caf50',
    600: '#43a047',
    700: '#388e3c',
    800: '#2e7d32',
    900: '#1b5e20',
  },
  yellow: {
    50: '#fffde7',
    100: '#fff9c4',
    200: '#fff59d',
    300: '#fff176',
    400: '#ffee58',
    500: '#ffeb3b',
    600: '#fdd835',
    700: '#fbc02d',
    800: '#f9a825',
    900: '#f57f17',
  },
  orange: {
    50: '#fff3e0',
    100: '#ffe0b2',
    200: '#ffcc80',
    300: '#ffb74d',
    400: '#ffa726',
    500: '#ff9800',
    600: '#fb8c00',
    700: '#f57c00',
    800: '#ef6c00',
    900: '#e65100',
  },
  red: {
    50: '#ffebee',
    100: '#ffcdd2',
    200: '#ef9a9a',
    300: '#e57373',
    400: '#ef5350',
    500: '#f44336',
    600: '#e53935',
    700: '#d32f2f',
    800: '#c62828',
    900: '#b71c1c',
  },
  
  // Neutral grays
  gray: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#eeeeee',
    300: '#e0e0e0',
    400: '#bdbdbd',
    500: '#9e9e9e',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
  
  // Chart-specific colors
  chart: {
    blue: '#2196f3',
    cyan: '#00bcd4',
    teal: '#009688',
    green: '#4caf50',
    lime: '#cddc39',
    yellow: '#ffeb3b',
    amber: '#ffc107',
    orange: '#ff9800',
    deepOrange: '#ff5722',
    red: '#f44336',
    pink: '#e91e63',
    purple: '#9c27b0',
    deepPurple: '#673ab7',
    indigo: '#3f51b5',
  },
};

// ============================================================================
// SEMANTIC TOKEN DEFINITIONS
// ============================================================================

/**
 * Background color presets for each intensity level
 */
const backgroundPresets = {
  light: {
    default: '#f7f7f7',                // Softer light background (was soft-light)
    paper: '#fcfcfc',
    elevated: '#fcfcfc',
    card: '#fcfcfc',
    panel: '#fcfcfc',
    sidebar: '#f9f9f9',
    dropdown: '#fcfcfc',
    input: '#ffffff',
    tableRow: '#fcfcfc',
    tableHeader: '#f3f3f3',
    tableRowAlt: '#f8f8f8',
  },
  'soft-light': {
    default: '#ebebeb',                // Darker soft light (more gray)
    paper: '#f2f2f2',
    elevated: '#f2f2f2',
    card: '#f2f2f2',
    panel: '#f2f2f2',
    sidebar: '#ededed',
    dropdown: '#f2f2f2',
    input: '#f8f8f8',
    tableRow: '#f2f2f2',
    tableHeader: '#e5e5e5',
    tableRowAlt: '#eaeaea',
  },
  'soft-dark': {
    default: '#1f2937',                // Softer dark (slate blue-ish)
    paper: '#2d3748',
    elevated: '#374151',
    card: '#2d3748',
    panel: '#2d3748',
    sidebar: '#2d3748',
    dropdown: '#374151',
    input: '#374151',
    tableRow: '#2d3748',
    tableHeader: '#374151',
    tableRowAlt: '#334155',
  },
  dark: {
    default: '#121212',                // Deep dark
    paper: '#1e1e1e',
    elevated: '#2a2a2a',
    card: '#1e1e1e',
    panel: '#1e1e1e',
    sidebar: '#1e1e1e',
    dropdown: '#2a2a2a',
    input: '#2a2a2a',
    tableRow: '#1e1e1e',
    tableHeader: '#2a2a2a',
    tableRowAlt: '#252525',
  },
};

export function getBackgroundPreset(intensity: ThemeIntensity) {
  return backgroundPresets[intensity];
}

/**
 * Creates semantic color tokens based on the current theme mode/intensity
 */
export const createColorTokens = (mode: PaletteMode, intensity?: ThemeIntensity) => {
  // Determine the effective intensity
  const effectiveIntensity: ThemeIntensity = intensity || mode;
  const isLightFamily = effectiveIntensity === 'light' || effectiveIntensity === 'soft-light';
  const bg = backgroundPresets[effectiveIntensity];
  
  // Border colors based on intensity
  const borderColors = {
    light: { default: '#d4d4d4', strong: baseColors.gray[400], subtle: '#e5e5e5' },
    'soft-light': { default: baseColors.gray[400], strong: baseColors.gray[500], subtle: baseColors.gray[300] },
    'soft-dark': { default: '#4b5563', strong: '#6b7280', subtle: '#374151' },
    dark: { default: baseColors.gray[700], strong: baseColors.gray[600], subtle: baseColors.gray[800] },
  };
  const borders = borderColors[effectiveIntensity];
  
  return {
    // ========================================================================
    // COMPONENT TOKENS
    // ========================================================================
    component: {
      card: {
        background: bg.card,
        border: borders.default,
        hover: isLightFamily ? baseColors.gray[50] : effectiveIntensity === 'soft-dark' ? '#374151' : baseColors.gray[800],
        shadow: isLightFamily ? 'rgba(0, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.4)',
      },
      modal: {
        background: bg.paper,
        overlay: isLightFamily ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.7)',
        border: borders.default,
        shadow: isLightFamily ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.5)',
      },
      panel: {
        background: bg.panel,
        backgroundAlt: isLightFamily ? baseColors.gray[50] : effectiveIntensity === 'soft-dark' ? '#374151' : '#252525',
        border: borders.default,
        header: isLightFamily ? baseColors.gray[100] : effectiveIntensity === 'soft-dark' ? '#374151' : '#2a2a2a',
      },
      sidebar: {
        background: bg.sidebar,
        backgroundAlt: isLightFamily ? baseColors.gray[50] : effectiveIntensity === 'soft-dark' ? '#374151' : '#252525',
        border: borders.default,
        hover: isLightFamily ? baseColors.gray[100] : effectiveIntensity === 'soft-dark' ? '#4b5563' : baseColors.gray[800],
        active: isLightFamily ? baseColors.blue[50] : baseColors.blue[900],
      },
      dropdown: {
        background: bg.dropdown,
        hover: isLightFamily ? baseColors.gray[100] : effectiveIntensity === 'soft-dark' ? '#4b5563' : baseColors.gray[700],
        selected: isLightFamily ? baseColors.blue[50] : baseColors.blue[900],
        border: isLightFamily ? baseColors.gray[300] : effectiveIntensity === 'soft-dark' ? '#4b5563' : baseColors.gray[600],
      },
      input: {
        background: bg.input,
        border: isLightFamily ? baseColors.gray[400] : effectiveIntensity === 'soft-dark' ? '#4b5563' : baseColors.gray[600],
        borderFocus: isLightFamily ? baseColors.blue[500] : baseColors.blue[400],
        placeholder: baseColors.gray[500],
        disabled: isLightFamily ? baseColors.gray[200] : effectiveIntensity === 'soft-dark' ? '#1f2937' : baseColors.gray[800],
      },
      table: {
        headerBackground: bg.tableHeader,
        rowBackground: bg.tableRow,
        rowBackgroundAlt: bg.tableRowAlt,
        rowHover: isLightFamily ? baseColors.blue[50] : effectiveIntensity === 'soft-dark' ? '#4b5563' : baseColors.gray[800],
        border: borders.default,
      },
      tooltip: {
        // Dark card in both themes — same chrome as RichTooltip. An inverted
        // light slab on a dark map is what operators were hovering through.
        background: '#1b1e26',
        text: '#e9ecf2',
        border: 'rgba(255,255,255,0.13)',
      },
    },

    // ========================================================================
    // BUTTON TOKENS
    // ========================================================================
    button: {
      primary: {
        background: baseColors.blue[500],
        backgroundHover: baseColors.blue[600],
        backgroundActive: baseColors.blue[700],
        backgroundDisabled: isLightFamily ? baseColors.gray[300] : baseColors.gray[700],
        text: '#ffffff',
        textDisabled: isLightFamily ? baseColors.gray[500] : baseColors.gray[600],
      },
      secondary: {
        background: baseColors.purple[500],
        backgroundHover: baseColors.purple[600],
        backgroundActive: baseColors.purple[700],
        backgroundDisabled: isLightFamily ? baseColors.gray[300] : baseColors.gray[700],
        text: '#ffffff',
        textDisabled: isLightFamily ? baseColors.gray[500] : baseColors.gray[600],
      },
      tertiary: {
        background: 'transparent',
        backgroundHover: isLightFamily ? baseColors.gray[100] : effectiveIntensity === 'soft-dark' ? '#4b5563' : baseColors.gray[800],
        backgroundActive: isLightFamily ? baseColors.gray[200] : effectiveIntensity === 'soft-dark' ? '#374151' : baseColors.gray[700],
        border: isLightFamily ? baseColors.gray[400] : effectiveIntensity === 'soft-dark' ? '#6b7280' : baseColors.gray[600],
        text: isLightFamily ? baseColors.gray[900] : baseColors.gray[100],
        textDisabled: isLightFamily ? baseColors.gray[400] : baseColors.gray[600],
      },
      success: {
        background: baseColors.green[600],
        backgroundHover: baseColors.green[700],
        backgroundActive: baseColors.green[800],
        text: '#ffffff',
      },
      warning: {
        background: baseColors.orange[600],
        backgroundHover: baseColors.orange[700],
        backgroundActive: baseColors.orange[800],
        text: '#ffffff',
      },
      error: {
        background: baseColors.red[600],
        backgroundHover: baseColors.red[700],
        backgroundActive: baseColors.red[800],
        text: '#ffffff',
      },
    },

    // ========================================================================
    // TEXT TOKENS
    // ========================================================================
    text: {
      primary: isLightFamily ? baseColors.gray[900] : baseColors.gray[100],
      secondary: isLightFamily ? baseColors.gray[700] : baseColors.gray[400],
      tertiary: isLightFamily ? baseColors.gray[600] : baseColors.gray[500],
      muted: isLightFamily ? baseColors.gray[500] : baseColors.gray[600],
      disabled: isLightFamily ? baseColors.gray[400] : baseColors.gray[700],
      inverse: isLightFamily ? '#ffffff' : baseColors.gray[900],
      link: baseColors.blue[isLightFamily ? 600 : 400],
      linkHover: baseColors.blue[isLightFamily ? 700 : 300],
      success: baseColors.green[isLightFamily ? 700 : 400],
      warning: baseColors.orange[isLightFamily ? 700 : 400],
      error: baseColors.red[isLightFamily ? 700 : 400],
      info: baseColors.blue[isLightFamily ? 600 : 400],
    },

    // ========================================================================
    // SEMANTIC STATE TOKENS
    // ========================================================================
    semantic: {
      success: {
        main: baseColors.green[isLightFamily ? 600 : 500],
        background: isLightFamily ? baseColors.green[50] : baseColors.green[900],
        border: isLightFamily ? baseColors.green[300] : baseColors.green[700],
        text: isLightFamily ? baseColors.green[800] : baseColors.green[200],
      },
      warning: {
        main: baseColors.orange[isLightFamily ? 600 : 500],
        background: isLightFamily ? baseColors.orange[50] : baseColors.orange[900],
        border: isLightFamily ? baseColors.orange[300] : baseColors.orange[700],
        text: isLightFamily ? baseColors.orange[800] : baseColors.orange[200],
      },
      error: {
        main: baseColors.red[isLightFamily ? 600 : 500],
        background: isLightFamily ? baseColors.red[50] : baseColors.red[900],
        border: isLightFamily ? baseColors.red[300] : baseColors.red[700],
        text: isLightFamily ? baseColors.red[800] : baseColors.red[200],
      },
      info: {
        main: baseColors.blue[isLightFamily ? 600 : 500],
        background: isLightFamily ? baseColors.blue[50] : baseColors.blue[900],
        border: isLightFamily ? baseColors.blue[300] : baseColors.blue[700],
        text: isLightFamily ? baseColors.blue[800] : baseColors.blue[200],
      },
    },

    // ========================================================================
    // CHART & DATA VISUALIZATION TOKENS
    // ========================================================================
    chart: {
      // Categorical colors for different data series
      categorical: [
        baseColors.chart.blue,
        baseColors.chart.orange,
        baseColors.chart.green,
        baseColors.chart.red,
        baseColors.chart.purple,
        baseColors.chart.cyan,
        baseColors.chart.pink,
        baseColors.chart.teal,
        baseColors.chart.amber,
        baseColors.chart.indigo,
        baseColors.chart.deepOrange,
        baseColors.chart.lime,
      ],
      
      // Sequential colors for gradients (light to dark)
      sequential: {
        blue: [
          baseColors.blue[100],
          baseColors.blue[300],
          baseColors.blue[500],
          baseColors.blue[700],
          baseColors.blue[900],
        ],
        green: [
          baseColors.green[100],
          baseColors.green[300],
          baseColors.green[500],
          baseColors.green[700],
          baseColors.green[900],
        ],
        red: [
          baseColors.red[100],
          baseColors.red[300],
          baseColors.red[500],
          baseColors.red[700],
          baseColors.red[900],
        ],
        purple: [
          baseColors.purple[100],
          baseColors.purple[300],
          baseColors.purple[500],
          baseColors.purple[700],
          baseColors.purple[900],
        ],
      },
      
      // Diverging colors for showing variance from a midpoint
      diverging: {
        redBlue: [
          baseColors.red[700],
          baseColors.red[400],
          baseColors.gray[300],
          baseColors.blue[400],
          baseColors.blue[700],
        ],
        orangePurple: [
          baseColors.orange[700],
          baseColors.orange[400],
          baseColors.gray[300],
          baseColors.purple[400],
          baseColors.purple[700],
        ],
      },
      
      // Chart-specific elements
      grid: isLightFamily ? baseColors.gray[300] : effectiveIntensity === 'soft-dark' ? '#4b5563' : baseColors.gray[700],
      axis: isLightFamily ? baseColors.gray[700] : baseColors.gray[400],
      label: isLightFamily ? baseColors.gray[800] : baseColors.gray[300],
      tooltip: {
        background: '#1b1e26',
        text: '#e9ecf2',
        border: 'rgba(255,255,255,0.13)',
      },
    },

    // ========================================================================
    // BACKGROUND TOKENS
    // ========================================================================
    background: {
      default: bg.default,
      paper: bg.paper,
      elevated: bg.elevated,
      overlay: isLightFamily ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.7)',
      backdrop: isLightFamily 
        ? 'rgba(255, 255, 255, 0.9)' 
        : effectiveIntensity === 'soft-dark' 
          ? 'rgba(45, 55, 72, 0.9)' 
          : 'rgba(30, 30, 30, 0.9)',
    },

    // ========================================================================
    // BORDER & DIVIDER TOKENS
    // ========================================================================
    border: {
      default: borders.default,
      strong: borders.strong,
      subtle: borders.subtle,
      focus: baseColors.blue[isLightFamily ? 500 : 400],
    },

    // ========================================================================
    // ELEVATION & SHADOW TOKENS
    // ========================================================================
    shadow: {
      sm: isLightFamily ? '0 1px 2px rgba(0, 0, 0, 0.05)' : '0 1px 2px rgba(0, 0, 0, 0.3)',
      md: isLightFamily ? '0 4px 6px rgba(0, 0, 0, 0.1)' : '0 4px 6px rgba(0, 0, 0, 0.4)',
      lg: isLightFamily ? '0 10px 15px rgba(0, 0, 0, 0.1)' : '0 10px 15px rgba(0, 0, 0, 0.5)',
      xl: isLightFamily ? '0 20px 25px rgba(0, 0, 0, 0.15)' : '0 20px 25px rgba(0, 0, 0, 0.6)',
    },
  };
};

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type ColorTokens = ReturnType<typeof createColorTokens>;

// Augment the MUI theme to include our custom tokens
declare module '@mui/material/styles' {
  interface Theme {
    tokens: ColorTokens;
    themeIntensity?: ThemeIntensity;
  }
  interface ThemeOptions {
    tokens?: ColorTokens;
    themeIntensity?: ThemeIntensity;
  }
}

